import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Download, Sparkles, Route as RouteIcon, ChevronRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import MapView from "@/components/MapView";

export default function ItineraryDetail() {
  const { slug } = useParams();
  const { t, tx, lang } = useI18n();
  const [it, setIt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/itineraries/${slug}`)
      .then(r => setIt(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-5xl mx-auto px-5 py-20 text-muted-foreground">{t.common.loading}</div>;
  if (error || !it) return (
    <div className="max-w-5xl mx-auto px-5 py-20">
      <p className="text-muted-foreground">{error || t.common.noResults}</p>
      <Link to="/itineraries" className="mt-4 inline-flex items-center gap-2 text-[hsl(var(--primary-green))]"><ArrowLeft className="w-4 h-4" /> {t.common.back}</Link>
    </div>
  );

  // Map data: markers from points
  const markers = it.points
    .filter(pt => pt.place && pt.place.latitude && pt.place.longitude)
    .map(pt => ({
      id: pt.place.id,
      slug: pt.place.slug,
      title: pt.place.title,
      short_description: pt.place.short_description,
      latitude: pt.place.latitude,
      longitude: pt.place.longitude,
      image_url: pt.place.image_url,
      category: pt.place.category,
    }));

  const mapData = markers.length > 0 ? {
    center: {
      lat: markers.reduce((s, m) => s + m.latitude, 0) / markers.length,
      lng: markers.reduce((s, m) => s + m.longitude, 0) / markers.length,
    },
    zoom: 14,
    markers,
    categories: [],
  } : null;

  return (
    <div>
      {/* Hero */}
      <div className="relative h-[55vh] min-h-[380px] overflow-hidden">
        {it.points[0]?.place?.image_url ? (
          <img src={it.points[0].place.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary-red))] to-[hsl(var(--primary-green))]" />
        )}
        <div className="absolute inset-0 hero-scrim" />
        <div className="relative h-full max-w-7xl mx-auto px-5 lg:px-8 flex flex-col justify-end pb-10">
          <Link to="/itineraries" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-4 w-fit">
            <ArrowLeft className="w-4 h-4" /> {t.common.back}
          </Link>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[hsl(var(--primary-red))] text-white text-xs font-semibold w-fit">
            <Sparkles className="w-3 h-3" /> AI itinerary
          </span>
          <h1 className="mt-3 font-display text-4xl md:text-6xl font-semibold tracking-tight text-white" data-testid="itinerary-detail-title">{it.title}</h1>
          {it.narrative_intro && <p className="mt-3 text-white/90 text-lg max-w-3xl">{it.narrative_intro}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-white text-sm">
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {it.duration_minutes} min</span>
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {it.points.length} stops</span>
            {it.estimated_distance_km > 0 && <span>{it.estimated_distance_km} km</span>}
            {it.recommended_start_time && <span>Start: {it.recommended_start_time}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-3 gap-10">
        {/* Points list */}
        <div className="lg:col-span-2 space-y-5">
          {it.pdf_file?.url && (
            <a
              href={fileUrl(it.pdf_file.url)}
              target="_blank"
              rel="noreferrer"
              data-testid="itinerary-download-pdf"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[hsl(var(--primary-red))] text-white font-semibold hover:bg-[hsl(var(--primary-red-hover))] transition"
            >
              <Download className="w-4 h-4" /> Download PDF guide
            </a>
          )}

          <h2 className="font-display text-2xl font-semibold tracking-tight mt-2">Itinerary</h2>

          <ol className="space-y-4">
            {it.points.map((pt, idx) => (
              <li key={idx} data-testid={`itin-point-${idx}`} className="relative pl-12">
                <span className="absolute left-0 top-0 w-9 h-9 rounded-full bg-[hsl(var(--primary-red))] text-white flex items-center justify-center font-bold">{pt.order}</span>
                {pt.place ? (
                  <Link to={`/places/${pt.place.slug}`} className="block bg-card border border-border rounded-2xl p-5 hover:border-[hsl(var(--primary-green))] hover:shadow-md transition">
                    <div className="flex gap-4">
                      {pt.place.image_url && (
                        <img src={pt.place.image_url} alt="" className="w-24 h-24 object-cover rounded-xl flex-shrink-0 hidden sm:block" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-semibold text-lg leading-tight">{tx(pt.place.title)}</h3>
                        {tx(pt.place.short_description) && <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{tx(pt.place.short_description)}</p>}
                        {pt.place.address && <p className="mt-1 text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {pt.place.address}</p>}
                        <div className="mt-2 flex items-center gap-3 text-xs">
                          <span className="text-muted-foreground"><Clock className="w-3 h-3 inline mr-1" /> {pt.stay_minutes} min</span>
                          <ChevronRight className="w-3 h-3 text-[hsl(var(--primary-green))]" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="bg-card border border-border rounded-2xl p-5">
                    <p className="text-sm">{pt.place_id}</p>
                  </div>
                )}
                {pt.visitor_tip && (
                  <p className="mt-2 text-sm bg-[hsl(var(--primary-green))]/10 border-l-4 border-[hsl(var(--primary-green))] px-3 py-2 rounded">
                    💡 <span className="italic">{pt.visitor_tip}</span>
                  </p>
                )}
                {pt.transition_to_next && idx < it.points.length - 1 && (
                  <p className="mt-2 text-xs text-muted-foreground italic ml-2">➤ {pt.transition_to_next}</p>
                )}
              </li>
            ))}
          </ol>

          {it.warnings && it.warnings.length > 0 && (
            <div className="mt-6 space-y-1">
              {it.warnings.map((w, i) => (
                <p key={i} className="text-sm bg-amber-50 border border-amber-200 px-3 py-2 rounded">⚠ {w}</p>
              ))}
            </div>
          )}
        </div>

        {/* Map sidebar */}
        <aside className="lg:sticky lg:top-24 h-fit">
          {mapData && (
            <div className="rounded-2xl overflow-hidden border border-border">
              <MapView data={mapData} height="500px" />
            </div>
          )}
          <div className="mt-4 p-5 bg-card border border-border rounded-2xl">
            <h3 className="font-display font-semibold mb-2">About this route</h3>
            <p className="text-sm text-muted-foreground"><strong>Profile:</strong> {it.profile}</p>
            <p className="text-sm text-muted-foreground"><strong>Type:</strong> {it.route_type}</p>
            <p className="text-sm text-muted-foreground"><strong>Language:</strong> {it.language?.toUpperCase()}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
